import { ref, readonly, computed } from "vue";
import type { Ref, ComputedRef } from "vue";
import { useSipBroadcast } from "@/components/SIPIntegration/composables/useSipBroadcast";
import { POPUP_WINDOW_NAME } from "@/components/SIPIntegration/composables/useSipOwnerLock";
import { decidePopupOpen } from "@/sip/sipPopupOpenGuard";
import type { PopupOpenDecision } from "@/sip/sipPopupOpenGuard";
import { isSipOwnerPresent } from "@/sip/sipOwnerPresence";
import { sipTelemetry } from "@/sip/sipTelemetry";
import type { SipPopupOpenSource } from "@/sip/sipTelemetry";

/**
 * Tempo massimo (ms) senza heartbeat dal popup prima di considerarlo morto.
 * Un valore breve permette alla main app di degradare velocemente a leader
 * inline; un valore troppo breve causa flapping (heartbeat = 2s).
 */
const HEARTBEAT_TIMEOUT_MS = 5_000;

/**
 * Intervallo di polling per controllare scadenza heartbeat e popup.closed.
 * popup.closed non emette eventi: la verifica via polling è necessaria.
 */
const POLL_INTERVAL_MS = 1_000;

/**
 * Altezza fissa della window popup: la barra SIP ha un'altezza naturale
 * limitata e non beneficia di una window più alta.
 */
const POPUP_HEIGHT = 220;

export interface UseSipPopupManagerReturn {
  /** True se la main app ha ricevuto heartbeat recente dal popup. */
  isPopupAlive: Readonly<Ref<boolean>>;
  /** True se la main app conosce un riferimento Window al popup. */
  hasPopupReference: ComputedRef<boolean>;
  /** Timestamp ms dell'ultimo heartbeat ricevuto (null se nessuno). */
  lastHeartbeatAt: Readonly<Ref<number | null>>;
  /**
   * True se un popup tiene il Web Lock owner: segnale di presenza immune al
   * throttling dei timer, a differenza di `isPopupAlive`. null = Web Locks non
   * disponibile o non ancora interrogata.
   */
  isOwnerLockHeld: Readonly<Ref<boolean | null>>;
  /**
   * Apre il popup SIP. DEVE essere chiamato da una gesture utente
   * (click), altrimenti il browser blocca window.open.
   * Ritorna true se il popup è stato aperto, false se bloccato.
   */
  openPopup: () => boolean;
  /** Porta in focus il popup esistente (no-op se non c'è riferimento). */
  focusPopup: () => void;
  /**
   * Chiude il popup esistente (no-op se già chiuso). Pubblica anche un
   * messaggio shutdown su BroadcastChannel così il popup si chiude da sé
   * anche se la main app non ha più il riferimento Window (post F5).
   */
  closePopup: (reason?: "role-changed" | "manual") => void;
  /**
   * Arma un listener una-tantum su pointerdown/keydown: alla prima gesture
   * utente, se la condizione è ancora vera e il popup non è già vivo, apre
   * il popup. Serve per aggirare il popup blocker: window.open programmatico
   * senza user-activation viene bloccato dal browser. Ritorna una funzione
   * cancel per disinnescare il listener prima che si attivi.
   */
  enableAutoOpenOnGesture: (condition: () => boolean) => () => void;
}

interface SipPopupManagerState {
  isPopupAlive: Ref<boolean>;
  popupWindow: Ref<Window | null>;
  lastHeartbeatAt: Ref<number | null>;
  /**
   * Ultimo esito noto di `isSipOwnerPresent()`: true/false, null se Web Locks non
   * è disponibile o non è ancora stata interrogata. Aggiornato dal poll e letto
   * SINCRONAMENTE al momento della gesture — un await prima di window.open
   * brucerebbe la user activation e farebbe scattare il popup blocker.
   */
  isOwnerLockHeld: Ref<boolean | null>;
  pollIntervalId: number | null;
  initialized: boolean;
}

// Singleton: una sola istanza di stato condivisa tra tutti i consumer della
// main app (App.vue, Header, SipIntegration in inline mode). Inizializzata
// al primo accesso.
let sharedState: SipPopupManagerState | null = null;

function buildPopupFeatures(): string {
  // Larghezza = metà dello schermo dell'utente, calcolata al momento
  // dell'apertura per adattarsi a setup multi-monitor / risoluzioni varie.
  const width = Math.max(420, Math.floor(window.screen.availWidth / 2));
  const left = Math.max(0, Math.floor((window.screen.availWidth - width) / 2));
  const top = Math.max(0, Math.floor((window.screen.availHeight - POPUP_HEIGHT) / 2));
  return [
    "popup=yes",
    `width=${width}`,
    `height=${POPUP_HEIGHT}`,
    `left=${left}`,
    `top=${top}`,
    "menubar=no",
    "toolbar=no",
    "location=no",
    "status=no",
    "resizable=yes",
    "scrollbars=no",
  ].join(",");
}

function ensureState(): SipPopupManagerState {
  if (sharedState) return sharedState;
  sharedState = {
    isPopupAlive: ref(false),
    popupWindow: ref<Window | null>(null),
    lastHeartbeatAt: ref<number | null>(null),
    isOwnerLockHeld: ref<boolean | null>(null),
    pollIntervalId: null,
    initialized: false,
  };
  return sharedState;
}

let sharedBroadcast: ReturnType<typeof useSipBroadcast> | null = null;

function refreshOwnerLock(state: SipPopupManagerState): void {
  void isSipOwnerPresent().then((held) => {
    state.isOwnerLockHeld.value = held;
  });
}

/**
 * Decide se `window.open(url, POPUP_WINDOW_NAME)` è lecito, ed emette telemetria
 * quando la navigazione di un popup vivo viene impedita (GAIA-2674).
 */
function resolvePopupOpen(
  state: SipPopupManagerState,
  source: SipPopupOpenSource
): PopupOpenDecision {
  const decision = decidePopupOpen({
    isPopupAlive: state.isPopupAlive.value,
    isOwnerLockHeld: state.isOwnerLockHeld.value,
  });
  if (decision.preventedNavigation) {
    sipTelemetry.popupNavigationPrevented({ source });
  }
  return decision;
}

/**
 * Porta in primo piano il popup esistente SENZA navigarlo. `window.open("", name)`
 * con URL vuoto riottiene il riferimento per name senza caricare nulla; il
 * broadcast è il fallback quando il focus programmatico è bloccato dal browser.
 */
function focusExistingPopup(state: SipPopupManagerState): void {
  if (state.popupWindow.value && !state.popupWindow.value.closed) {
    try {
      state.popupWindow.value.focus();
    } catch {
      // ignore: alcune policy del browser bloccano focus()
    }
  } else {
    // Reference persa (es. dopo un F5 della main app): re-acquisiamo la Window per
    // name. Se NON esiste, window.open apre un about:blank da chiudere subito.
    let reopened: Window | null = null;
    try {
      reopened = window.open("", POPUP_WINDOW_NAME);
    } catch {
      reopened = null;
    }
    if (reopened && reopened !== window) {
      try {
        if (reopened.location.href === "about:blank") {
          reopened.close();
        } else {
          state.popupWindow.value = reopened;
          try {
            reopened.focus();
          } catch {
            // ignore
          }
        }
      } catch {
        // Accesso a location bloccato (cross-origin): improbabile su same-origin.
        // La window.open per name ha già tentato il focus, lasciamo passare.
      }
    }
  }

  sharedBroadcast?.postFocusRequest();
}

function initializeIfNeeded(state: SipPopupManagerState): void {
  if (state.initialized) return;
  state.initialized = true;

  // Subscribe ai messaggi BroadcastChannel pubblicati dal popup.
  // useSipBroadcast richiede onBeforeUnmount: lo creiamo qui in un setup
  // context "virtuale" — funziona finché chiamiamo useSipPopupManager DENTRO
  // un componente Vue (cosa garantita dai consumer: App.vue, Header, etc.).
  sharedBroadcast = useSipBroadcast();

  sharedBroadcast.onHeartbeat((msg) => {
    if (!msg.isPopup) return;
    state.lastHeartbeatAt.value = msg.timestamp;
    state.isPopupAlive.value = true;
  });

  sharedBroadcast.onPopupClosing(() => {
    // Notifica esplicita dal popup: degrada subito senza aspettare polling.
    state.isPopupAlive.value = false;
    state.lastHeartbeatAt.value = null;
    state.popupWindow.value = null;
  });

  // Polling timer: rileva scadenza heartbeat e chiusura della Window (popup.closed).
  // setInterval + clearInterval gestito qui a livello modulo perché lo stato è
  // singleton globale, non legato a un singolo componente.
  state.pollIntervalId = window.setInterval(() => {
    const now = Date.now();

    // Sempre, prima di ogni early return: il lock owner è l'unico segnale che
    // resta valido quando i timer del popup sono throttlati (GAIA-2674). Questo
    // poll gira nella main app in foreground, quindi non è throttlato lui stesso.
    refreshOwnerLock(state);

    if (state.popupWindow.value && state.popupWindow.value.closed) {
      state.popupWindow.value = null;
      state.isPopupAlive.value = false;
      state.lastHeartbeatAt.value = null;
      return;
    }

    if (state.lastHeartbeatAt.value === null) return;
    if (now - state.lastHeartbeatAt.value > HEARTBEAT_TIMEOUT_MS) {
      state.isPopupAlive.value = false;
      state.lastHeartbeatAt.value = null;
    }
  }, POLL_INTERVAL_MS);

  // Probe di liveness all'avvio: chiede SUBITO uno snapshot. Un popup già aperto
  // (anche in un browsing context group diverso, es. una scheda aperta a parte)
  // risponde con un heartbeat immediato che marca isPopupAlive=true entro pochi
  // ms — prima della prima gesture utente — così l'auto-open NON ricarica né
  // duplica il popup esistente via window.open(url, name).
  sharedBroadcast.requestSnapshot();

  // La probe però dipende dal fatto che il popup PROCESSI il messaggio: se il
  // browser l'ha congelato, la risposta non arriva. Interroghiamo subito anche il
  // lock owner, che non dipende dal popup per essere osservato.
  refreshOwnerLock(state);
}

/**
 * Apre il popup SIP solo se il singleton è inizializzato (main app) e non esiste
 * già un popup. Ritorna true se ha effettivamente chiamato window.open su una
 * finestra NUOVA, false in tutti gli altri casi (popup già esistente, singleton
 * non disponibile — es. chiamato dalla stessa finestra popup — oppure window.open
 * bloccato).
 *
 * Deve essere chiamato da un click handler (user gesture) altrimenti il popup
 * blocker del browser può bloccare window.open.
 */
export function openSipPopupIfClosed(): boolean {
  const state = sharedState;
  if (!state) return false; // non inizializzato (es. chiamato dalla window popup) → no-op
  // Il click su un numero di telefono non deve MAI navigare un popup vivo: il
  // prefill lo raggiunge comunque via BroadcastChannel (GAIA-2674).
  if (resolvePopupOpen(state, "phone-click").action === "focus") return false;
  // Riferimento vivo (appena aperto, heartbeat non ancora ricevuto): non riaprire.
  if (state.popupWindow.value && !state.popupWindow.value.closed) return false;
  const url = `${window.location.origin}/sip-bar`;
  const win = window.open(url, POPUP_WINDOW_NAME, buildPopupFeatures());
  if (!win) return false;
  state.popupWindow.value = win;
  win.focus();
  return true;
}

export function useSipPopupManager(): UseSipPopupManagerReturn {
  const state = ensureState();
  initializeIfNeeded(state);

  /**
   * Intento MANUALE "apri il telefono". Se un popup esiste già — anche solo secondo
   * il Web Lock, con l'heartbeat muto per throttling — NON lo naviga: lo porta in
   * primo piano. Ritorna false solo quando window.open è stato bloccato dal
   * browser, così l'invoker può mostrare il proprio fallback.
   */
  function openPopup(): boolean {
    if (resolvePopupOpen(state, "manual").action === "focus") {
      focusExistingPopup(state);
      return true;
    }
    const url = `${window.location.origin}/sip-bar`;
    const features = buildPopupFeatures();
    const win = window.open(url, POPUP_WINDOW_NAME, features);
    if (!win) {
      // Popup blocker o policy del browser: l'invoker mostrerà un fallback UI.
      return false;
    }
    state.popupWindow.value = win;
    win.focus();
    return true;
  }

  function focusPopup(): void {
    // Strategia in cascata per portare il popup in primo piano: il singolo
    // window.focus() spesso non basta (reference persa dopo un F5 della main app,
    // limiti anti-abuse sul focus stealing, browsing context group diverso).
    // Nessun ramo naviga la finestra: vedi focusExistingPopup.
    focusExistingPopup(state);
  }

  function closePopup(reason: "role-changed" | "manual" = "manual"): void {
    // Pubblica shutdown via BroadcastChannel come fallback per il caso in cui
    // non abbiamo riferimento alla Window (es. main app refreshata dopo che
    // il popup era già aperto: il singleton manager reset perde popupWindow,
    // ma il popup esiste ancora come finestra del browser).
    sharedBroadcast?.postPopupShutdown(reason);

    if (state.popupWindow.value) {
      if (!state.popupWindow.value.closed) {
        try {
          state.popupWindow.value.close();
        } catch {
          // ignore: alcune policy del browser bloccano close() su window
          // non aperte da questo script. Il messaggio broadcast la chiude.
        }
      }
      state.popupWindow.value = null;
    }
    state.isPopupAlive.value = false;
    state.lastHeartbeatAt.value = null;
  }

  const hasPopupReference = computed(() => state.popupWindow.value !== null);

  function enableAutoOpenOnGesture(condition: () => boolean): () => void {
    let cancelled = false;
    const events = ["pointerdown", "keydown"] as const;

    function tryOpen(): void {
      if (cancelled) return;
      // Un popup esiste già: niente da auto-aprire e — soprattutto — niente da
      // navigare. Prima di GAIA-2674 qui si guardava solo l'heartbeat: col popup
      // throttlato in background bastava un click qualsiasi nella main app per
      // ricaricarlo, tagliando la chiamata in corso. Nessun focus: l'auto-open non
      // è un intento esplicito dell'utente, non deve rubare il primo piano.
      if (resolvePopupOpen(state, "auto-open").action === "focus") {
        cleanup();
        return;
      }
      if (!condition()) {
        // Condizione non più valida (utente non più teleseller, ecc.).
        // Lasciamo il listener attivo: potrebbe tornare valida dopo un
        // cambio ruolo. Niente da fare per ora.
        return;
      }
      const opened = openPopup();
      if (opened) cleanup();
    }

    function cleanup(): void {
      events.forEach((ev) => document.removeEventListener(ev, tryOpen, true));
    }

    events.forEach((ev) =>
      document.addEventListener(ev, tryOpen, {
        capture: true,
        passive: true,
      })
    );

    return () => {
      cancelled = true;
      cleanup();
    };
  }

  return {
    isPopupAlive: readonly(state.isPopupAlive),
    hasPopupReference,
    lastHeartbeatAt: readonly(state.lastHeartbeatAt),
    isOwnerLockHeld: readonly(state.isOwnerLockHeld),
    openPopup,
    focusPopup,
    closePopup,
    enableAutoOpenOnGesture,
  };
}
